import { Badge, type BadgeProps } from '@/components/ui/badge';
import { STATUS_CONFIG } from '@/constants/config';
import type {
  BookingStatus,
  DriverStatus,
  KYCStatus,
  PaymentStatus,
  TicketStatus,
  TicketPriority,
} from '@/types';

type StatusType =
  | 'booking'
  | 'driver'
  | 'kyc'
  | 'payment'
  | 'ticket'
  | 'priority';

interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  type: StatusType;
  status: string;
  showDot?: boolean;
}

type StatusConfigItem = { label: string; color: string };

export function StatusBadge({
  type,
  status,
  showDot = true,
  className,
  ...props
}: StatusBadgeProps) {
  const config = STATUS_CONFIG[type] as Record<string, StatusConfigItem> | undefined;
  const statusConfig = config?.[status];

  if (!statusConfig) {
    return (
      <Badge variant="muted" {...props}>
        {status}
      </Badge>
    );
  }

  return (
    <Badge
      variant={statusConfig.color as BadgeProps['variant']}
      dot={showDot}
      className={className}
      {...props}
    >
      {statusConfig.label}
    </Badge>
  );
}

// Specialized status badges
export function BookingStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps, 'type' | 'status'> & { status: BookingStatus }) {
  return <StatusBadge type="booking" status={status} {...props} />;
}

export function DriverStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps, 'type' | 'status'> & { status: DriverStatus }) {
  return <StatusBadge type="driver" status={status} {...props} />;
}

export function KYCStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps, 'type' | 'status'> & { status: KYCStatus }) {
  return <StatusBadge type="kyc" status={status} {...props} />;
}

export function PaymentStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps, 'type' | 'status'> & { status: PaymentStatus }) {
  return <StatusBadge type="payment" status={status} {...props} />;
}

export function TicketStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps, 'type' | 'status'> & { status: TicketStatus }) {
  return <StatusBadge type="ticket" status={status} {...props} />;
}

export function PriorityBadge({
  priority,
  ...props
}: Omit<StatusBadgeProps, 'type' | 'status'> & { priority: TicketPriority }) {
  return <StatusBadge type="priority" status={priority} showDot={false} {...props} />;
}
